import { blue, neutral, signal } from './tokens'

// Semantic roles from docs/STYLE.md section 3.1, in the hyphenated naming Tamagui 3 uses
// (`bg="background hover:background-hover"`, `className="text-color-secondary"`).
// Light only: the owner asked for light mode everywhere, so there is no dark theme and no
// prefers-color-scheme switch. STYLE.md keeps the dark values documented for when that changes.
const roles = {
  background: neutral.white,
  'background-subtle': neutral['neutral-50'],
  'background-muted': neutral['neutral-100'],
  surface: neutral.white,
  'surface-raised': neutral.white,
  color: neutral['neutral-900'],
  'color-secondary': neutral['neutral-700'],
  'color-tertiary': neutral['neutral-600'],
  'color-placeholder': neutral['neutral-550'],
  'border-color': neutral['neutral-200'],
  'border-color-subtle': neutral['neutral-100'],
  'border-color-strong': neutral['neutral-300'],
  'border-color-control': neutral['neutral-500'],
  'border-color-danger': signal['red-600'],
  primary: blue['blue-600'],
  'primary-hover': blue['blue-700'],
  'primary-press': blue['blue-800'],
  'primary-soft': blue['blue-50'],
  'primary-soft-hover': blue['blue-100'],
  'on-primary': neutral.white,
  'primary-text': blue['blue-600'],
  link: blue['blue-700'],
  'focus-ring': blue['blue-600'],
  success: signal['green-700'],
  'success-soft': signal['green-50'],
  'on-success-soft': signal['green-800'],
  warning: signal['amber-600'],
  'warning-soft': signal['amber-50'],
  'on-warning-soft': signal['amber-800'],
  danger: signal['red-600'],
  'danger-solid': signal['red-600'],
  'danger-solid-hover': signal['red-700'],
  'on-danger-solid': neutral.white,
  'danger-soft': signal['red-50'],
  'on-danger-soft': signal['red-800'],
  info: blue['blue-700'],
  'info-soft': blue['blue-50'],
  'on-info-soft': blue['blue-800'],
  'disabled-bg': neutral['neutral-100'],
  'disabled-text': neutral['neutral-500'],
  overlay: 'rgba(18, 24, 38, 0.48)',
}

// The five request states from STYLE.md 3.1. Each one is background + text + border + dot, so a
// badge is `bg-status-neu-bg text-status-neu-fg border-status-neu-border` with no hex anywhere.
// `bezahlt` is the only solid one (end state, strongest emphasis). The dot is decoration
// (aria-hidden) - the label always carries the meaning, because red and green alone are not
// distinguishable for everyone. Text contrast is 7.08:1 or better, verified by scripts/contrast.py.
const status = {
  'status-neu-bg': blue['blue-50'],
  'status-neu-fg': blue['blue-800'],
  'status-neu-border': blue['blue-200'],
  'status-neu-dot': blue['blue-600'],
  'status-angebot-bg': signal['amber-50'],
  'status-angebot-fg': signal['amber-800'],
  'status-angebot-border': signal['amber-100'],
  'status-angebot-dot': signal['amber-600'],
  'status-bestaetigt-bg': signal['green-50'],
  'status-bestaetigt-fg': signal['green-800'],
  'status-bestaetigt-border': signal['green-100'],
  'status-bestaetigt-dot': signal['green-600'],
  'status-abgelehnt-bg': signal['red-50'],
  'status-abgelehnt-fg': signal['red-800'],
  'status-abgelehnt-border': signal['red-100'],
  'status-abgelehnt-dot': signal['red-600'],
  'status-bezahlt-bg': signal['green-700'],
  'status-bezahlt-fg': neutral.white,
  'status-bezahlt-border': signal['green-700'],
  'status-bezahlt-dot': neutral.white,
}

// The keys Tamagui's own components (Input, Button, Sheet, ...) read.
const tamaguiAliases = {
  'background-hover': roles['background-subtle'],
  'background-press': roles['background-muted'],
  'background-focus': roles.surface,
  'background-strong': roles['background-subtle'],
  'background-transparent': 'rgba(0,0,0,0)',
  'color-hover': roles.color,
  'color-press': roles.color,
  'color-focus': roles.color,
  'color-transparent': 'rgba(0,0,0,0)',
  'border-color-hover': roles['border-color-strong'],
  'border-color-press': roles['border-color-strong'],
  'border-color-focus': roles['focus-ring'],
  'placeholder-color': roles['color-placeholder'],
  'outline-color': roles['focus-ring'],
  'shadow-color': 'rgba(18,24,38,0.18)',
  'shadow-color-hover': 'rgba(18,24,38,0.24)',
}

// Tamagui's own components and `settings.selectionStyles` read a numbered 1-12 step scale
// (color-1 = quietest surface ... color-12 = strongest text). Replacing v6's themes wholesale drops
// it, which logs `[tamagui] unknown color "color-4"` and leaves text selection unstyled. Mapped onto
// the STYLE.md neutral scale so a Tamagui component lands on our palette instead of a default grey.
const numberedSteps = {
  'color-1': neutral.white,
  'color-2': neutral['neutral-25'],
  'color-3': neutral['neutral-50'],
  'color-4': neutral['neutral-100'],
  'color-5': neutral['neutral-200'],
  'color-6': neutral['neutral-300'],
  'color-7': neutral['neutral-400'],
  'color-8': neutral['neutral-500'],
  'color-9': neutral['neutral-550'],
  'color-10': neutral['neutral-600'],
  'color-11': neutral['neutral-700'],
  'color-12': neutral['neutral-900'],
}

const light = { ...numberedSteps, ...roles, ...status, ...tamaguiAliases }

// Button tones per STYLE.md: blue is the only action color, there is no orange CTA.
const buttonTones = {
  light_primary: {
    ...light,
    background: roles.primary,
    'background-hover': roles['primary-hover'],
    'background-press': roles['primary-press'],
    'background-focus': roles['primary-hover'],
    color: roles['on-primary'],
    'color-hover': roles['on-primary'],
    'color-press': roles['on-primary'],
    'border-color': roles.primary,
    'border-color-hover': roles['primary-hover'],
  },
  light_secondary: {
    ...light,
    background: roles.surface,
    'background-hover': roles['background-subtle'],
    'background-press': roles['background-muted'],
    color: roles.color,
    'border-color': roles['border-color-control'],
    'border-color-hover': roles['border-color-strong'],
  },
  light_ghost: {
    ...light,
    background: 'rgba(0,0,0,0)',
    'background-hover': roles['primary-soft'],
    'background-press': roles['primary-soft-hover'],
    color: roles['primary-text'],
    'border-color': 'rgba(0,0,0,0)',
    'border-color-hover': 'rgba(0,0,0,0)',
  },
}

export const themes = { light, ...buttonTones }
export const lightRoles = roles
export const statusRoles = status
